<template>
  <v-app :class="[{ 'is-authenticated': isAuthed }]">
    <!-- Lightweight navigation progress bar -->
    <v-progress-linear
      v-show="isRouting"
      indeterminate
      absolute
      location="top"
      height="3"
      color="primary"
      bg-color="transparent"
      class="navigation-progress"
    />

    <!-- The shell is OUTSIDE RouterView on purpose.
         Nested inside it, nothing rendered until navigation resolved — and on
         a guarded route that waits for Auth0's silent-auth round trip, so a
         cold launch showed a bare background for the whole exchange. Hoisted,
         the nav, header and skeletons paint immediately and the view fills in
         underneath. `shellRoute` is the route we're heading TO (see below),
         which is what makes this work before navigation commits. -->
    <AppShell v-if="showShell">
      <RouterView v-slot="{ Component }">
        <KeepAlive>
          <component :is="Component" />
        </KeepAlive>
      </RouterView>
    </AppShell>
    <RouterView v-else v-slot="{ Component }">
      <component :is="Component" />
    </RouterView>

    <OfflineIndicator />
    <PwaUpdatePrompt />
    <InstallPrompt />

    <!-- E2EE unlock prompt. Renders as a modal whenever the user has opted
         into encryption but this tab hasn't been unlocked yet. The dialog
         is non-dismissable: closing it would leave the rest of the app
         showing encrypted-NULL columns, which is useless without the
         decryption keys. -->
    <v-dialog
      :model-value="isAuthed && enc.isLocked.value"
      persistent
      max-width="540"
      class="unlock-dialog"
    >
      <UnlockDialog @unlocked="enc.load(auth)" @signout="logoutFromLock" />
    </v-dialog>

    <!-- In-app feedback. Mounted once here so anything (sidebar, an error
         state offering "report this") can open it via openFeedback() without
         each surface owning its own copy of the form. -->
    <v-dialog
      v-if="isAuthed"
      v-model="feedbackOpen"
      max-width="560"
      class="feedback-dialog"
    >
      <FeedbackDialog />
    </v-dialog>
  </v-app>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onBeforeUnmount, ref, watch, KeepAlive } from 'vue'
import { RouterView, useRouter } from 'vue-router'
import AppShell from '@/components/shell/AppShell.vue'
import OfflineIndicator from '@/components/Global/OfflineIndicator.vue'
import PwaUpdatePrompt from '@/components/Global/PwaUpdatePrompt.vue'
import InstallPrompt from '@/components/Global/InstallPrompt.vue'
import { useQueryClient } from '@tanstack/vue-query'
import { globalState, setDisplayName } from '@/stores/globalState'
import { useAuth } from '@/composables/useAuth'
import { authenticatedGet } from '@/utils/api'
import UnlockDialog from '@/components/UnlockDialog.vue'
import { useEncryption } from '@/composables/useEncryption'
import { useFeedback } from '@/composables/useFeedback'
import { useSealedMerge } from '@/composables/useSealedMerge'

// Async: nobody has the feedback form open on first paint, so it shouldn't
// ride along in the entry chunk. VDialog doesn't render its body until opened,
// so the chunk is fetched on first use.
const FeedbackDialog = defineAsyncComponent(() => import('@/components/FeedbackDialog.vue'))

// Hydrate Auth0 + identity into globalState.
const auth = useAuth() as any

const isAuthed = computed(() => globalState.isAuthenticated)

// v-dialog needs a writable model; the composable owns the shared state so
// openFeedback() works from anywhere.
const { isOpen: feedbackOpen } = useFeedback()

// Encryption state: refresh from the server whenever auth flips on. The
// composable itself is reactive; we only call `load` to populate it.
// `isLocked` then drives the UnlockDialog modal in the template.
const enc = useEncryption()
const queryClient = useQueryClient()
// Track whether we've seen serverEnabled=true so we can wipe the stale
// aggregation caches the moment the server tells us the user is
// encrypted. TanStack's persist client otherwise paints localStorage
// snapshots from the un-encrypted era (typically: "$0 / Personal
// scopes") forever, even after a hard reload.
let lastServerEnabled = false
watch(
  () => enc.state.serverEnabled,
  (now) => {
    if (now && !lastServerEnabled) {
      queryClient.removeQueries({ queryKey: ['tracked-categories'] })
      queryClient.removeQueries({ queryKey: ['pending-total'] })
      queryClient.removeQueries({ queryKey: ['spending-comparison'] })
      queryClient.removeQueries({ queryKey: ['expense-details'] })
    }
    lastServerEnabled = !!now
  },
  { immediate: true },
)
watch(isAuthed, async (authed, prev) => {
  if (!authed) {
    // In-memory lock ONLY — never lockAndForget() here. This watcher cannot
    // tell a deliberate sign-out from an involuntary session loss (tenant
    // session expiry, a refresh token Auth0 rejected, a silent-renewal
    // iframe blocked by third-party-cookie policy in Firefox/Safari). The
    // Auth0 SDK clears its own token cache on those failures, so isAuthed
    // drops to false either way, and forgetting the device key here made
    // every such blip demand the 24-word recovery phrase after re-login.
    // Deliberate sign-out scrubs the key in useAuth's logoutUser(); so do
    // "Lock this device" and "Disable + erase" in EncryptionSettings.
    // (prev === true still guards the initial hydrating fire, where there is
    // nothing unlocked to lock.)
    if (prev === true) enc.lock()
    return
  }
  await enc.load(auth)
}, { immediate: true })

async function logoutFromLock(): Promise<void> {
  // Forfeit the session if the user refuses to unlock — we don't ship a
  // half-encrypted view of the app. The composable exposes this method
  // as `logoutUser` (not `logout` — that's the raw Auth0 SDK call which
  // doesn't go through the local cleanup the composable does), so use it
  // directly. The earlier `auth.logout?.()` was a silent no-op.
  try {
    await auth.logoutUser?.()
  } catch (err) {
    console.error('[UnlockDialog] sign out failed:', err)
  }
}

// ------------------------------------------------------------------
// Session epoch — bump SESSION_EPOCH after auth-breaking changes (e.g. the
// 2026-06-12 MFA Action refresh-token bug) to force every signed-in user
// through one clean re-auth. We drop the Auth0 SDK's cached tokens and
// redirect through Auth0; with a live SSO cookie that round-trip is silent
// (no password), and opted-in users get their MFA challenge as intended.
// ------------------------------------------------------------------
const SESSION_EPOCH = '2026-06-12'
const EPOCH_KEY = 'lp-session-epoch'

watch(
  [() => auth.isLoading?.value, isAuthed],
  ([loading, authed]: [boolean, boolean]) => {
    if (loading) return
    if (localStorage.getItem(EPOCH_KEY) === SESSION_EPOCH) return
    localStorage.setItem(EPOCH_KEY, SESSION_EPOCH)
    if (!authed) return // nothing cached to fix; stamp and move on
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith('@@auth0spajs@@')) localStorage.removeItem(k)
    }
    auth
      .login({ mode: 'login' })
      .catch((e: unknown) => console.error('[App] epoch re-auth failed:', e))
  },
  { immediate: true },
)

// Hydrate the user-editable display name from the backend on first
// authenticated load. Once cached in localStorage (see globalState), reloads
// render the sidebar pill immediately and skip the network round-trip —
// /api/auth/status here was a measurable share of cold-load time. Cross-device
// updates resync on the next fresh login (AuthCallback re-fetches), and
// in-app edits write through setDisplayName immediately.
watch(isAuthed, async (authed) => {
  if (!authed) return
  if (globalState.displayName) return // already hydrated from localStorage
  try {
    const res = await authenticatedGet('/api/auth/status', {
      getApiToken: auth.getApiToken,
      getAccessTokenSilently: auth.getAccessTokenSilently,
      isAuthenticated: auth.isAuthenticated,
      isLoading: auth.isLoading,
    })
    if (res.ok) {
      const data = await res.json()
      setDisplayName(data.display_name ?? null)
    }
  } catch (err) {
    console.warn('[App] display name hydration failed:', err)
  }
}, { immediate: true })

// Routes that render without the shell (public marketing, legal,
// auth callback, onboarding flow). Everything else gets the shell.
const BARE_ROUTES = new Set([
  'Home',
  'Auth0Callback',
  'PrivacyPolicy',
  'TermsOfService',
  'NotFound',
])

// Structurally typed rather than RouteLocationNormalized: this is fed both by
// beforeEach (normalized) and by router.resolve (resolved, whose `name` is
// nullable), and the only thing read is the name.
function usesShell(route: { name?: unknown } | null | undefined): boolean {
  const name = route?.name?.toString()
  if (!name) return false
  if (BARE_ROUTES.has(name)) return false
  return true
}

const router = useRouter()
const isRouting = ref(false)

// Which route the shell decision is made against.
//
// It can't be useRoute(): during the auth guard, navigation hasn't committed,
// so the current route is still the previous one (on a cold launch, a
// nameless placeholder) and the shell would stay hidden for exactly the
// stretch we're trying to fill. Seeded instead by resolving the URL the
// browser actually opened, then kept current by beforeEach.
//
// The seed is what covers the cold-launch case. App.vue's beforeEach is
// registered after the auth guard in router.ts, so on a suspended first
// navigation it may not run until the guard resolves — too late to be useful.
const shellRoute = ref<{ name?: unknown } | null>(
  (() => {
    try {
      return router.resolve(window.location.pathname + window.location.search)
    } catch {
      return null
    }
  })(),
)
const showShell = computed(() => usesShell(shellRoute.value))

router.beforeEach((to, from, next) => {
  shellRoute.value = to
  if (from.name && to.fullPath !== from.fullPath) isRouting.value = true
  next()
})
router.afterEach((to, from) => {
  if (to.fullPath !== from.fullPath) isRouting.value = false
})
router.onError(() => { isRouting.value = false })

onBeforeUnmount(() => { isRouting.value = false })

// Sealed rows a Plaid sync couldn't finish updating get merged here, quietly,
// whenever this client is unlocked — the server can't do it, and making the
// user remember a button in Settings isn't a design. See useSealedMerge.
const stopSealedMerge = useSealedMerge({
  getApiToken: auth.getApiToken,
  getAccessTokenSilently: auth.getAccessTokenSilently,
  isAuthenticated: auth.isAuthenticated,
  // Without this the context can't tell "signed out" from "still booting".
  isLoading: auth.isLoading,
} as any).start()
onBeforeUnmount(stopSealedMerge)
</script>

<style scoped>
.navigation-progress { z-index: 9999; }
</style>
